import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import type { RecipeV2Doc, RecipeV2Summary } from '@ctcells/api'
import type { BatchAssignment, RecipeListRow, Sample } from '@ctcells/contract'
import { maskPii, t } from '@ctcells/i18n'

import { Button } from '../../atoms/Button.tsx'
import { DataTable, type Column } from '../../atoms/DataTable.tsx'
import { EmptyState } from '../../atoms/EmptyState.tsx'
import { Field } from '../../atoms/Field.tsx'
import { useModalBusy } from '../../atoms/modalFrame.ts'
import { NumberStepper } from '../../atoms/NumberStepper.tsx'
import { Radio } from '../../atoms/Radio.tsx'
import { useSubmitValidation } from '../../atoms/useSubmitValidation.ts'
import { TextInput } from '../../atoms/TextInput.tsx'
import { PageHeader } from '../../layout/PageHeader.tsx'
import { SettingsRow } from '../../layout/SettingsList.tsx'
import { canView, gate, type ScreenAccess } from '../access.ts'
import { AddActions, FormError, FormSection, ModalFooter } from '../formSections.tsx'
import { DEMO_UNASSIGNED_SAMPLES } from '../demoData.ts'
import type { RecipeVocabularyNote } from '../recipes/recipeVocabulary.ts'
import { RecipeVocabularyReport } from './RecipeVocabularyReport.tsx'
import { NewSampleForm, WellSamplePicker } from './NewBatchSamplePopups.tsx'
import { RecipeDetailPopup, type RecipeDetailDoc } from './NewBatchRecipePopup.tsx'
import { TRAY_SLOTS } from '../monitoring/monitoringModel.ts'
import { BatchTray } from './batchPlanParts.tsx'
import { wellSampleColumns } from './wellSampleColumns.ts'
import { planSampleColumns, recipePlanColumns, shortSampleId } from './batchPlanColumns.tsx'
import {
  assignmentsBody,
  clearWell,
  groupPatients,
  patientLockFor,
  pickedInWellOrder,
  pickWell,
  slotWells,
  type WellPicks,
  wellHolder,
} from './newBatchAssignments.ts'
import {
  BATCH_NAME_ACCEPTED,
  BATCH_NAME_MAX,
  batchNameBody,
  batchNameTooLong,
  ignoredBatchName,
} from './newBatchName.ts'
import { sampleHints } from '../samples/sampleFormatHints.ts'
import type { SampleDraft } from '../samples/SampleNewScreen.tsx'

import '../screens.css'
import './newBatch.css'

/**
 * 새 작업 — 와이어프레임 2225행(2026-09-16 재디자인: 한 열 폼 + 구역 3개).
 *
 * 🔴 **「시작」 단계·버튼을 넣지 않는다.** eric 원문: *"상단에서만 시작할거야"* — 시작 창구는
 *    상시 작업 줄 하나다(§1-4 · 1015행 주석). 스테퍼도 3단계(시료 · 레시피 · 사전 점검)다.
 * 🔴 **「임시 저장」이 없다.** 정본 근거가 없고 와이어프레임에도 0건이다(§1-3).
 *
 * 🔴 **2026-09-20 — 응답에 근거가 없는 자리 셋을 걷었다**(eric 원칙 · 팀장 결정).
 *    ① 「챔버 배정」 트레이 그림 ② 「자동 배정」 버튼 ③ 「담당」 행.
 *    당시 `POST /batches` 본문은 `{command_id, recipe_id, sample_ids}` 셋뿐이라 칸 배정도
 *    담당자도 받지 않았다. 그림을 남기면 **사람이 못 정하는 값을 정할 수 있는 것처럼** 보인다.
 *    ⊕ **정정 2026-09-26** — 서버가 `assignments`(시료 ↔ 웰)를 받게 됐다(신설 2026-09-22 ·
 *      `curl` 로 201 · `sample.assigned_well`/`sample_ref` 가 서는 것 확인). 그래서 **웰 배정 구역**
 *      (`AssignmentBlock`)이 섰다 — 사람이 웰을 고른다. ②「자동 배정」과 ③「담당」은 여전히 근거가
 *      없다(자동은 추론이고, 담당은 본문에 없다). 되살리지 마라.
 * 🔴 **사전 점검 9항목 고정 격자도 걷었다.** `PreflightResult` 는 `passed` + `blockers` 뿐이고
 *    항목 목록은 와이어프레임에서 온 것이라 **어느 응답에도 없었다**(`PreflightReport` 주석).
 *
 * 시료는 여기서 **등록도 하고 담기도 한다** — 서버 모델이 그렇다
 * (`POST /samples` 로 등록하고 `POST /batches` 의 `sample_ids` 로 담는다).
 *
 * ⚠ **시료 수를 화면이 막지 않는다.** 서버는 `minItems: 1` 만 집행하고 상한을 재지 않는다
 *   (`CreateBatchBody` 실측). 계약 설명문의 「최대 8 + Ref 1」은 트레이가 9칸인 **물리 안내**라
 *   담은 수 옆에 안내로만 쓴다 — **차단은 서버 몫**이다.
 */
export interface NewBatchScreenProps {
  /** 작업 추가 창에서 상단과 같은 제어락 획득 UI. live 컨테이너만 연결한다. */
  lockControl?: ReactNode
  /**
   * 아직 어느 작업에도 안 들어간 시료.
   * 🔴 판정 축은 **`batch_id` 하나다** — 서버 `unassignable_samples` 가 그것만 본다.
   */
  available?: readonly Sample[]
  /** 기존 작업 소속 시료는 선택 불가 사유와 확인 경로만 제공한다. */
  assignedSamples?: readonly Sample[]
  onInspectAssigned?: ((batchId: string) => void) | undefined
  /**
   * 「새 시료」 칸의 종류 · 암종 · 암 병기 **제안 출처** — 이미 등록된 시료 전부(배정 여부 무관 · 2026-10-01 회의
   * 「직접 입력 + 이전 입력값 자동완성」). 시료 추가 폼과 같은 `sampleHints` 로 거른다. 🔴 제안일 뿐 — 목록 밖 값도 그대로 나간다.
   * 안 넘기면 `available` 에서 뽑는다. 목록을 못 받았으면(403 · 실패) 빈 배열 — 직접 입력은 그대로다.
   */
  hintSamples?: readonly Sample[] | undefined
  /**
   * 고를 수 있는 레시피. 🔴 **`committed` 만 넘긴다** — 서버가 그것만 받는다
   * (`committed_version()` 이 `draft`·`archived` 를 422 로 떨어뜨린다). 거르는 곳은 컨테이너다.
   */
  recipes?: readonly RecipeListRow[]
  /**
   * 레시피 id → 그 본문의 **스텝 어휘 대조** 결과(`recipeVocabularyNote`). 알릴 것이 없는 레시피는 키가 없다.
   * 🔴 **표 대조일 뿐 판정이 아니다** — 생성·점검 버튼을 막지 않는다. 최종 권위는 서버의 사전 점검·시작 게이트다.
   */
  vocabularyNotes?: Readonly<Record<string, RecipeVocabularyNote>>
  /**
   * 고른 레시피가 바뀔 때 — 컨테이너가 그 문서의 시료 자리(트레이 그림)를 묻는다. 🔴 고르기 상태는 여전히 화면이 쥔다
   */
  onRecipeChange?: ((recipeId: string) => void) | undefined
  /**
   * 고른 레시피의 **문서와 서버 요약**(`GET /recipes` 항목의 `payload` · `POST /recipes/summary`). 창 안에서 두 자리가 읽는다:
   * - 시료 구역의 트레이 그림 — 요약의 `samples`(P1~P4 · REF ↔ 웰). 🔴 「어느 시료가 어느 칸」은 그리지 않는다 — 그 값은 응답에 없다
   * 안 넘기면(안 골랐다 · 새 형식 문서가 아니다) 그림은 빈 칸이다.
   * ⚠ 레시피 본문은 이제 표 아래가 아니라 줄 끝 [상세] 팝업이다(`recipeDetail` · eric 2026-10-21 「아래에.. 길게 넣잖아.. 팝업창을 띄워줘」)
   */
  recipeDoc?:
    | {
        loading: boolean
        doc: RecipeV2Doc
        summary?: RecipeV2Summary | undefined
        /** 못 물은 까닭(권한 · 통신) — 그림 아래 · 본문 자리에만 한 줄(창 전체를 막지 않는다) */
        notice?: string | undefined
      }
    | undefined
  /**
   * 레시피 표 줄 끝 [상세]를 눌렀을 때 — 컨테이너가 그 레시피 id 로 문서 · 요약을 묻는다(고르지 않은 레시피도). 팝업을 닫으면 `undefined`
   */
  onRecipeDetail?: ((recipeId: string | undefined) => void) | undefined
  /** [상세] 팝업이 읽는 그 레시피의 문서 · 요약. `recipeId` 가 지금 연 팝업과 다르면 낡은 값이라 쓰지 않는다 */
  recipeDetail?: RecipeDetailDoc | undefined
  /**
   * 들어올 때 미리 골라 둘 레시피. 레시피 상세의 「이 레시피로 작업 만들기」가 주소로 넘긴다
   * (`BATCH_NEW_RECIPE_PARAM`).
   *
   * 🔴 **고르기 편의일 뿐이다.** 여기 실린 id 가 `recipes` 에 없으면 **고른 것이 없는 것과
   *    같다** — 「작업 만들기」가 서지 않는다. 주소에 아무 값이나 실어도 만들 수 없다.
   * ⚠ 첫 렌더에만 쓴다. 그 뒤에는 사람이 고른 것이 이긴다.
   */
  initialRecipeId?: string | undefined
  loading?: boolean
  /**
   * 목록이 비어 있는 **사유**. 🔴 **「없다」와 「못 봤다」를 가른다.**
   *
   * `GET /samples` 는 **operator/admin 한정**이라(`samples.py:81 READ_ROLES`) viewer 에게
   * 403 이다. 사유가 없으면 고르개가 「미배정 시료 없음」이라고 **단언**한다 — 못 보는 것을
   * 0건이라 말하는 거짓이다. ⚠ `loading` 도 `false` 라 회색 스켈레톤조차 안 뜬다.
   *
   * ⚠ **이 화면의 문은 이미 닫혀 있다** — viewer 는 `control` 이 없어 작업 목록의
   *   「＋ 새 작업」이 **비활성 + 사유**이고(`BatchesScreen`), 여기 들어와도 「작업 만들기」가
   *   같은 `controlGate` 로 막힌다. 사유는 그래도 단다: 주소를 직접 친 사람이 **왜** 비는지
   *   알아야 하고, 규약이 「숨기지 않고 비활성 + 사유」이기 때문이다.
   */
  notices?: { samples?: string | undefined; recipes?: string | undefined }
  access?: ScreenAccess
  /** 만들어진 작업의 id. **서버가 준 뒤에만** 값이 생긴다 — 사전 점검의 선행 조건이다 */
  createdBatchId?: string | undefined
  /**
   * 만들어진 작업의 **서버가 저장한 이름**(201 응답 `Batch.name`). 사람이 적은 이름과 다르면
   * 「서버가 이름을 받지 않았다」를 알린다(`ignoredBatchName` · `docs/backend-requests.csv` BR-94).
   */
  createdBatchName?: string | null | undefined
  /**
   * 서버가 작업 이름을 받는가(BR-94 · 기본 `BATCH_NAME_ACCEPTED` = 지금은 `false`). 🔴 받지 않으면 이름 칸이 비활성 + 사유이고 키를 싣지 않는다.
   * ⚠ 시험이 «받는 날»의 동작(이름 인자 · 「버려졌다」 알림)을 잴 때만 넘긴다 — 컨테이너는 넘기지 않는다.
   */
  nameAccepted?: boolean | undefined
  /** 요청 중. 중복 클릭을 막는다(멱등키가 매번 새로 난다) */
  creating?: boolean
  /** 생성 실패 문구. 🔴 **폼이 닫히지 않고 그 자리에 뜬다** */
  createError?: string | undefined
  onCancel?: () => void
  /**
   * 창 안에서는 **사람이 웰마다 고른 것**(eric 2026-10-27 「해당 칸 또는 웰 별로」)으로 전건일 때만 `assignments` 를 싣는다.
   *    `sampleIds` 는 레시피 웰 차례다. 레시피를 안 골랐거나 시료 자리를 못 받았으면 인자가 없다 — 서버는 배정 없이 만든다(종전).
   */
  onCreate?: (
    recipeId: string,
    sampleIds: readonly string[],
    /** 사람이 적은 작업 이름(걷은 값). 비었으면 `undefined` — 서버가 조립한다(배정도 없으면 인자 자체가 없다) */
    name?: string,
    /** 시료 ↔ 웰 배정 — 전건일 때만 온다. 없으면 인자 자체가 없다 */
    assignments?: readonly BatchAssignment[],
  ) => void
  /** 만들어진 작업 상세로 */
  onOpenBatch?: () => void
  /** 레시피가 0건일 때 — 「레시피 만들기」 */
  onNewRecipe?: () => void
  /**
   * 시료 등록(페이지 판 「새 시료」 폼만). 없으면 등록 버튼이 비활성된다(demo 경로).
   */
  onRegisterSample?: (draft: SampleDraft) => void
  registerError?: string | undefined
  registering?: boolean
  /**
   * `modal` — 창(`Modal`) 안에 들어갈 때. 제목 · 「← 작업」 · 취소는 창의 머리줄과 × 가 맡으므로
   * 여기서는 폼만 그린다(eric 2026-09-22 「새 작업을 팝업창으로」). 기본은 전체 화면 한 장이다.
   */
  frame?: 'page' | 'modal' | undefined
}

export function NewBatchScreen({
  lockControl,
  available = DEMO_UNASSIGNED_SAMPLES,
  assignedSamples = [],
  onInspectAssigned,
  hintSamples,
  recipes = [],
  vocabularyNotes = {},
  onRecipeChange,
  recipeDoc,
  onRecipeDetail,
  recipeDetail,
  initialRecipeId,
  loading = false,
  notices,
  access = {},
  createdBatchId,
  createdBatchName,
  nameAccepted = BATCH_NAME_ACCEPTED,
  creating = false,
  createError,
  onCancel,
  onCreate,
  onOpenBatch,
  onNewRecipe,
  onRegisterSample,
  registerError,
  registering = false,
  frame = 'page',
}: NewBatchScreenProps) {
  const [picked, setPicked] = useState<readonly string[]>([])
  const [wellPicks, setWellPicks] = useState<WellPicks>({})
  /** 창 안: 시료 선택 창을 연 웰 */
  const [openWell, setOpenWell] = useState<string | undefined>()
  /**
   * 사람이 방금 고른 레시피 — 그 시료 자리가 오면 첫 웰의 선택 창을 바로 연다(eric 2026-10-27 「레시피를 클릭하면.. 바로 A1 선택되고. 시료
   * 선택하는 창이 보이게 해줘」). 자리는 비동기로 오므로 상태로 쥐고 «파생»한다(효과로 상태를 다시 쓰지 않는다)
   */
  const [autoOpenFor, setAutoOpenFor] = useState<string | undefined>()
  /** 창 안 레시피 표 상자(세 줄 높이 · 넘치면 안에서 구른다) */
  const recipeBox = useRef<HTMLDivElement>(null)
  /** 선택 창을 닫으면 포커스를 돌려줄 자리(누른 웰 · 또는 배정 표의 그 줄 단추) */
  const wellAnchor = useRef<HTMLElement | SVGElement | null>(null)
  const [recipeId, setRecipeId] = useState(initialRecipeId ?? '')
  /* 고른 레시피 줄이 상자 밖이면 그 줄이 보이게 — 상자만 굴린다(창 본문은 그대로) */
  useEffect(() => {
    const box = recipeBox.current
    const row = box?.querySelector<HTMLElement>('tr[aria-selected="true"]')
    if (box == null || row == null) return
    const head = box.querySelector('thead')?.getBoundingClientRect().height ?? 0
    const top = row.offsetTop - head
    if (top < box.scrollTop) box.scrollTop = top
    else if (row.offsetTop + row.offsetHeight > box.scrollTop + box.clientHeight)
      box.scrollTop = row.offsetTop + row.offsetHeight - box.clientHeight
  }, [recipeId])
  const [pickerOpen, setPickerOpen] = useState(false)
  const [formOpen, setFormOpen] = useState(false)
  const [batchName, setBatchName] = useState('')
  /** [상세] 팝업이 연 레시피 — 고른 레시피와 따로다 */
  const [detailId, setDetailId] = useState<string | undefined>(undefined)
  /* 🔴 만드는 중에는 창이 × · Esc · 바깥 누름으로 닫히지 않는다. 창 밖(페이지)에서는 아무 일도 안 한다 */
  useModalBusy(creating)

  const view = canView(access)
  const framed = frame === 'modal'
  const controlGate = gate(access, { perm: 'control' })
  const hints = useMemo(() => sampleHints(hintSamples ?? available), [hintSamples, available])
  const free = available.filter((s) => !picked.includes(s.sample_id))
  const chosen = picked
    .map((id) => available.find((s) => s.sample_id === id))
    .filter((s): s is Sample => s !== undefined)
  const recipe = recipes.find((r) => r.recipe_id === recipeId)
  const recipeLabel =
    recipe === undefined ? undefined : `${recipe.name} · v${String(recipe.version)}`
  /** 만든 뒤에는 입력을 잠근다 — 서버가 이미 그 조합으로 작업을 세웠다 */
  const made = createdBatchId !== undefined
  /*
   * 시료 ↔ 웰 — 창 안에서 고른 레시피의 시료 자리를 받았을 때만 **사람이 웰마다 고른다**(eric 2026-10-27 · `pickWell`).
   * 🔴 자동 배정 · 순서 추론이 없다. 고른 시료(`sample_ids`)는 레시피 웰 차례로 읽는다(`pickedInWellOrder`).
   * 🔴 페이지(장비 관점 콘솔)는 그림이 없어 정하지도 싣지도 않는다 — 보이지 않는 배정을 보내지 않는다(종전 `picked` 그대로).
   */
  const slotGroups = framed && recipe !== undefined ? recipeDoc?.summary?.samples : undefined
  /** 시료 자리 수(웰 수) — 「시료 n / 9」의 분모 */
  const capacity = slotGroups === undefined ? undefined : slotWells(slotGroups).length
  /** 고른 시료 — 창 안은 웰에 든 것(웰 차례) · 페이지는 담은 목록 */
  const selected: readonly string[] = framed
    ? slotGroups === undefined
      ? []
      : pickedInWellOrder(wellPicks, slotGroups)
    : picked
  const assignment = slotGroups === undefined ? undefined : assignmentsBody(selected, wellPicks)
  /*
   * 시료 구역 잠금(창 안) — 레시피가 칸을 정하므로 레시피 먼저(eric 2026-10-21 「시료는 레시피 선택 후에 선택 가능하게」).
   * 안 골랐으면 「레시피를 먼저 고르세요」, 고른 레시피의 시료 자리를 받는 중 · 못 받았으면 그 까닭. 🔸 고른 레시피에 물을 문서가
   * 없으면(`recipeDoc` 없음 — 새 형식이 아님) 칸을 모르는 채 종전처럼 연다 — 그 레시피로 아예 못 만드는 막다른 길을 두지 않는다
   */
  const sampleLock: string | undefined = !framed
    ? undefined
    : recipe === undefined
      ? t('reason.pickRecipeFirst')
      : recipeDoc === undefined || slotGroups !== undefined
        ? undefined
        : (recipeDoc.notice ?? t('state.loading'))
  /** 그 웰에 든 시료. 없으면 `undefined` */
  const holderOf = (wellName: string): string | undefined => wellHolder(wellPicks, wellName, '')
  /** 트레이 웰 안 작은 글씨 — 그 웰에 고른 시료 번호(마스킹 뒤 줄인 꼴) */
  const captionOf = (_sampleRef: string, wellName: string): string | undefined => {
    const id = holderOf(wellName)
    return id === undefined ? undefined : shortSampleId(maskPii(id, view))
  }
  /** 웰 단추의 접근 이름 — 「P2 · A2 · 비어 있음」 / 「P2 · A2 · fe-…」 */
  const wellLabel = (sampleRef: string, wellName: string): string => {
    const id = holderOf(wellName)
    return id === undefined
      ? t('batchNew.well.vacant', { ref: sampleRef, well: wellName })
      : t('batchNew.well.filled', { ref: sampleRef, well: wellName, sample: maskPii(id, view) })
  }
  /** 웰의 시료 자리 이름(P1 · REF) */
  const refOfWell = (wellName: string): string | undefined =>
    slotGroups?.find((group) => group.wells.includes(wellName))?.sample_ref
  /** 고른 시료의 환자 번호(원래 값 · 가리기 전) — 목록에 없는 시료면 `false`(대조하지 않는다) */
  const patientOf = (sampleId: string): string | null | undefined | false => {
    const sample = available.find((s) => s.sample_id === sampleId)
    return sample === undefined ? false : sample.patient_id
  }
  /*
   * 한 묶음 = 한 환자(eric 2026-10-29 「P1 두 개의 환자 번호가 같아야 하잖아」 → 「너 생각에 좋은 방법으로 해줘」) — 묶음마다 든 시료의 환자.
   * 배정 표는 묶음 덩어리로 묶어 환자 번호 칸을 한 번만 세우고, 웰 선택 창은 같은 묶음의 다른 웰에 든 환자로 좁히며, 어긋나면 [추가]를 막는다
   */
  const groupInfo = slotGroups === undefined ? [] : groupPatients(slotGroups, wellPicks, patientOf)
  const mismatchRefs = groupInfo.filter((g) => g.mismatch).map((g) => g.sampleRef)
  /** 트레이 그림 아래 한 줄 — 레시피 안 고름 · 못 받음 · 받는 중 */
  const trayReason =
    recipe === undefined
      ? t('batchNew.tray.pickRecipe')
      : recipeDoc?.notice !== undefined
        ? recipeDoc.notice
        : recipeDoc?.loading === true
          ? t('state.loading')
          : slotGroups === undefined || slotGroups.length === 0
            ? t('batchNew.tray.noSlots')
            : undefined

  /* 🔴 서버가 이름을 받지 않으면(BR-94) 칸이 잠겨 있고 키도 싣지 않는다 — 적은 값이 남아 있어도 보내지 않는다 */
  const sentName = nameAccepted ? batchNameBody(batchName) : undefined
  const validationRoot = useRef<HTMLDivElement>(null)
  const validation = useSubmitValidation(
    (value: { name: string; recipeId: string; selected: readonly string[]; picks: WellPicks }) => ({
      name:
        nameAccepted && batchNameTooLong(value.name)
          ? t('reason.batchNameTooLong', { max: BATCH_NAME_MAX })
          : undefined,
      recipe: recipes.some((row) => row.recipe_id === value.recipeId)
        ? undefined
        : t('validation.recipeRequired'),
      samples:
        value.selected.length === 0 ||
        (slotGroups !== undefined && assignmentsBody(value.selected, value.picks).kind !== 'full')
          ? t(
              slotGroups === undefined
                ? 'validation.samplesRequired'
                : 'validation.wellsUnassigned',
            )
          : undefined,
    }),
  )
  const validationValues = { name: batchName, recipeId, selected, picks: wellPicks }
  const nameTooLong = nameAccepted && batchNameTooLong(batchName)
  const nameField = framed
    ? validation.field('name')
    : {
        fieldProps: {
          htmlFor: 'batch-name',
          error: nameTooLong ? t('reason.batchNameTooLong', { max: BATCH_NAME_MAX }) : undefined,
          errorId: 'batch-name-error',
        },
        inputProps: {
          id: 'batch-name',
          invalid: nameTooLong,
          describedBy: nameTooLong ? 'batch-name-error' : undefined,
        },
      }
  const recipeField = validation.field('recipe')
  const sampleField = validation.field('samples')
  /** 만든 뒤에만 잰다 — 입력이 잠겨 있어 지금 칸의 값이 곧 보낸 값이다 */
  const nameIgnored = made ? ignoredBatchName(sentName, createdBatchName) : undefined

  const add = (id: string) => setPicked((ids) => (ids.includes(id) ? ids : [...ids, id]))
  const remove = (id: string) => {
    setPicked((ids) => ids.filter((v) => v !== id))
  }
  const pickRecipe = (id: string) => {
    /* 웰은 레시피마다 다르다 — 바꾸면 웰마다 고른 것을 비운다(남기면 없는 웰의 배정이 남는다) · 새 레시피의 첫 웰 창을 연다 */
    setWellPicks({})
    validation.change('recipe', { ...validationValues, recipeId: id })
    setOpenWell(undefined)
    setAutoOpenFor(id)
    wellAnchor.current = null
    setRecipeId(id)
    onRecipeChange?.(id)
  }

  /* 시료 열 넷은 작업 상세 서랍과 같은 부품이다(`batchPlanParts.tsx`) — 이 창은 끝에 [제거]만 덧붙인다 */
  const pickedColumns: Column<Sample>[] = [
    ...planSampleColumns(view),
    {
      key: 'remove',
      header: '',
      align: 'end',
      nowrap: true,
      cell: (s) => (
        <Button
          onClick={() => remove(s.sample_id)}
          size="pill"
          {...(made
            ? ({ disabled: true, disabledReason: t('reason.batchAlreadyCreated') } as const)
            : controlGate)}
        >
          {t('action.remove')}
        </Button>
      ),
    },
  ]

  const freeColumns: Column<Sample>[] = [
    {
      key: 'sampleId',
      header: t('column.sampleId'),
      nowrap: true,
      cell: (s) => <span className="mono">{maskPii(s.sample_id, view)}</span>,
    },
    {
      key: 'patientId',
      header: t('column.patientId'),
      nowrap: true,
      cell: (s) => <span className="mono">{maskPii(s.patient_id, view)}</span>,
    },
    { key: 'type', header: t('column.sampleType'), cell: (s) => s.sample_type ?? t('mask.none') },
    {
      key: 'add',
      header: '',
      align: 'end',
      nowrap: true,
      cell: (s) => (
        <Button
          onClick={() => add(s.sample_id)}
          size="pill"
          variant="primary"
          {...(made
            ? ({ disabled: true, disabledReason: t('reason.batchAlreadyCreated') } as const)
            : controlGate)}
        >
          {t('action.pick')}
        </Button>
      ),
    },
  ]

  const recipePickGate = made
    ? ({ disabled: true, disabledReason: t('reason.batchAlreadyCreated') } as const)
    : controlGate
  const openDetail = (id: string | undefined) => {
    setDetailId(id)
    onRecipeDetail?.(id)
  }
  /*
   * 창 안: 줄 맨 앞 라디오(하나만 · 접근 이름 = 레시피 이름) · 줄 끝 [상세] 팝업(eric 2026-10-21 「라디오 버튼을 하나만 선택하게 해주고..
   * 각 행에서.. 상세 버튼을 만들고」). 줄 아무 곳을 눌러도 고른다(`onRowClick` — 라디오 · [상세]에서 시작한 누름은 표가 거른다).
   * 페이지(장비 관점 콘솔)는 종전 이름 단추 그대로다.
   */
  /* 이름 · 버전 · 생성은 작업 상세 서랍과 같은 부품이다(`recipePlanColumns`) — 이 창은 이름 칸을 라디오로 · 끝에 [상세]를 단다 */
  const recipeRadioColumns: Column<RecipeListRow>[] = [
    ...recipePlanColumns<RecipeListRow>({
      key: 'pick',
      header: t('column.name'),
      nowrap: true,
      cell: (row) => (
        <Radio
          checked={row.recipe_id === recipeId}
          label={row.name}
          name="batch-new-recipe"
          onChange={() => pickRecipe(row.recipe_id)}
          value={row.recipe_id}
          {...recipePickGate}
        />
      ),
    }),
    {
      key: 'detail',
      header: '',
      align: 'end',
      nowrap: true,
      cell: (row) => (
        <Button
          aria-label={t('batchNew.recipe.detailOf', { name: row.name })}
          onClick={() => openDetail(row.recipe_id)}
          size="pill"
        >
          {t('batchNew.recipe.detail')}
        </Button>
      ),
    },
  ]
  const detailRow = recipes.find((r) => r.recipe_id === detailId)

  const recipeColumns: Column<RecipeListRow>[] = recipePlanColumns<RecipeListRow>({
    key: 'pick',
    header: '',
    nowrap: true,
    cell: (row) => (
      <Button
        onClick={() => pickRecipe(row.recipe_id)}
        selected={row.recipe_id === recipeId}
        size="pill"
        {...(made
          ? ({ disabled: true, disabledReason: t('reason.batchAlreadyCreated') } as const)
          : controlGate)}
      >
        {row.name}
      </Button>
    ),
  })

  /**
   * 🔴 **생성 선행조건은 「레시피 1 + 시료 1 이상」뿐이다.** 그 둘은 서버가 `422` 로 집행하는
   *    것을 화면이 미리 말해 주는 자리이고, 그 밖의 수(9건 등)는 **서버가 재지 않으므로 막지 않는다.**
   * 🔴 제어락이 필요하다 — `POST /batches` 는 `require_control_lock` 대상이다(실측).
   */
  const createGate = made
    ? ({ disabled: true, disabledReason: t('reason.batchAlreadyCreated') } as const)
    : creating
      ? ({ disabled: true, disabledReason: t('reason.requestInFlight') } as const)
      : gate(access, {
          perm: 'control',
          needsLock: true,
          /*
           * 🔴 **고른 레시피가 «목록에 있을 때»만 선다**(2026-09-21). 종전에는 `recipeId !== ''`
           *    였는데 그때는 id 가 표에서만 왔다. 이제 주소로도 들어오므로(사전 선택) 빈 문자열이
           *    아닌 것만으로는 부족하다 — 목록에 없는 id 로 버튼이 서면 서버가 422 로 떨구고
           *    사람은 화면에서 「골라 놓은 것」을 본 적이 없어 까닭을 모른다.
           */
        })
  const createGateFinal =
    framed && !createGate.disabled && mismatchRefs.length > 0
      ? {
          disabled: true as const,
          disabledReason: t('reason.groupPatientMismatch', { refs: mismatchRefs.join(', ') }),
        }
      : !framed && !createGate.disabled
        ? recipe === undefined || selected.length === 0
          ? { disabled: true as const, disabledReason: t('reason.needRecipeAndSample') }
          : nameAccepted && batchNameTooLong(batchName)
            ? {
                disabled: true as const,
                disabledReason: t('reason.batchNameTooLong', { max: BATCH_NAME_MAX }),
              }
            : createGate
        : createGate

  const submit = () => {
    if (createGateFinal.disabled) return
    if (framed && !validation.validate(validationValues, validationRoot.current)) {
      // 빈 웰은 선택 창을 닫고 배정 표에서 강조한다. 부분 시료 작업은 기존처럼 허용한다.
      setOpenWell(undefined)
      setAutoOpenFor(undefined)
      return
    }
    if (assignment?.kind === 'full')
      onCreate?.(recipeId, selected, sentName, assignment.assignments)
    else if (sentName !== undefined) onCreate?.(recipeId, selected, sentName)
    else onCreate?.(recipeId, selected)
  }

  /*
   * 창 안의 표 셋(담은 시료 · 기존 시료 · 레시피)은 남는 폭을 열이 똑같이 나눈다(eric 2026-09-30 「팝업 창이 너무 좁아」 —
   * 넓힌 창에서 자동 배분은 아이디 열 둘이 폭을 다 가져가 타입 · 단계가 한쪽에 몰렸다). 페이지는 종전 배분 그대로다.
   */
  const tableWidths = frame === 'modal' ? ({ columnWidths: 'even' } as const) : {}
  /*
   * 창 안의 표는 다른 목록 화면과 같은 표(`a-tbl--managed`)다(eric 2026-10-21 「레시피 추가 화면 느낌」 · 10-20 「다른 항목 리스트에 맞게」).
   * 페이지(장비 관점 콘솔)는 종전 조밀한 표 그대로다.
   */
  const density = framed ? 'managed' : 'compact'

  /* 창 안 표 셋은 아래 「N건」 줄을 세우지 않는다 — 건수는 구역 제목이 말한다(시료 추가 창 목록 상자와 같은 꼴) */
  const plainTable = (
    <DataTable
      caption={t('stepper.newBatch.sample')}
      columns={pickedColumns}
      density={density}
      {...(framed ? { hideCount: true } : {})}
      {...tableWidths}
      emptyLabel={sampleLock ?? t('state.empty.pickedSamples')}
      rowKey={(s) => s.sample_id}
      rows={sampleLock === undefined ? chosen : []}
    />
  )
  const newSampleGate = made
    ? ({ disabled: true, disabledReason: t('reason.batchAlreadyCreated') } as const)
    : controlGate
  const sampleActions = (
    <div className="btnrow" style={{ paddingTop: 'var(--space-md)' }}>
      <Button onClick={() => setPickerOpen((v) => !v)} selected={pickerOpen} size="pill">
        {t('action.pickExisting')}
      </Button>
      <span style={{ marginLeft: 'auto' }}>
        <Button
          onClick={() => setFormOpen((v) => !v)}
          selected={formOpen}
          size="pill"
          {...newSampleGate}
        >
          {t('action.newSample')}
        </Button>
      </span>
    </div>
  )
  const freeLabel =
    notices?.samples ?? (loading ? t('state.loading') : t('state.empty.freeSamples'))
  /** 웰 시료 선택 창 안의 고르기 · 빼기 — 만든 뒤 · 만드는 중에는 막는다 */
  const wellGate = made
    ? ({ disabled: true, disabledReason: t('reason.batchAlreadyCreated') } as const)
    : creating
      ? ({ disabled: true, disabledReason: t('reason.requestInFlight') } as const)
      : controlGate
  /** 선택 창을 닫고 누른 웰로 포커스를 돌린다 */
  const closeWell = () => {
    setOpenWell(undefined)
    setAutoOpenFor(undefined)
    wellAnchor.current?.focus()
  }
  const openWellAt = (wellName: string, focusBack: HTMLElement | SVGElement) => {
    wellAnchor.current = focusBack
    setAutoOpenFor(undefined)
    setOpenWell(wellName)
  }
  /** 배정 표의 줄을 눌렀다 — 트레이의 그 웰을 누른 것과 같다(같은 창) */
  const openWellFromRow = (wellName: string, from: HTMLElement) => openWellAt(wellName, from)
  const pickable = sampleLock === undefined && slotGroups !== undefined
  /*
   * 지금 열린 웰 — 사람이 누른 웰, 없으면 방금 고른 레시피의 «첫 웰»(레시피 웰 차례의 첫째 · 하드코딩 아님).
   * 🔴 저절로 연 창은 포커스를 옮기지 않는다 — 레시피 라디오를 화살표로 훑는 동안 포커스를 뺏지 않게(창만 보인다)
   */
  const firstWell = slotGroups === undefined ? undefined : slotWells(slotGroups)[0]?.wellName
  const autoWell =
    openWell === undefined && pickable && autoOpenFor !== undefined && autoOpenFor === recipeId
      ? firstWell
      : undefined
  const shownWell = openWell ?? autoWell
  const openRef = shownWell === undefined ? undefined : refOfWell(shownWell)
  /** 배정 표 칸 하나 — 값이 있으면 그 글자(넘치면 말줄임 + 가리키면 전체) · 없으면 짧은 대시 가운데 */
  const slotCell = (value: string | undefined, mono = false) =>
    value === undefined ? (
      <span className="nbslots__vacant">{t('mask.none')}</span>
    ) : (
      <span className={mono ? 'mono nbslots__text' : 'nbslots__text'} title={value}>
        {value}
      </span>
    )
  const sampleColumns = wellSampleColumns()
  /*
   * 트레이 오른쪽 배정 표 — 레시피 시료 자리마다 한 줄(웰 · 묶음 · 시료 · eric 2026-10-27 「오른쪽에 전에처럼 선택한 시료 보이게 해줘.. 9개 시료 칸
   * 넣어 원래처럼」). 고른 시료 번호는 «전체»로 선다(트레이 칸은 줄인 꼴). 빈 칸은 짧은 대시 가운데. 줄의 시료 칸을 누르면 그 웰의 선택 창이 뜨고,
   * 열린 웰의 줄은 강조색이다. 🔴 끌어 놓기 · 담은 순서 자동 배정은 되살리지 않는다 — 웰마다 사람이 고른다
   */
  /*
   * 트레이 오른쪽 배정 표 — 레시피 시료 자리마다 한 줄(웰 · 묶음 + 시료 열 `wellSampleColumns` · eric 2026-10-27 「오른쪽에 전에처럼 선택한 시료
   * 보이게 해줘.. 9개 시료 칸 넣어 원래처럼」 · 「환자번호랑 암종 같은 정보도 같이」). 고른 시료 번호는 «전체»로 선다(트레이 칸은 줄인 꼴).
   * 🔴 **줄 자리는 늘 트레이 칸 수(9)만큼 미리 잡는다** — 레시피를 고르기 전에도 빈 틀이 서고, 고르면 그 안이 찬다(「예전처럼 시료 9개 칸을
   *    미리 여유를 놓고.. 트레이가 고정되어야지.. 왜 움직여?」). 열 폭은 고정이고 긴 값은 말줄임 + 가리키면 전체(`newBatch.css` `.nbslots`).
   * 🔴 환자 번호는 PII 가림(`maskPii`) 그대로. 줄의 시료 번호 칸을 누르면 그 웰의 선택 창이 뜨고, 열린 웰의 줄은 강조색이다.
   *    끌어 놓기 · 담은 순서 자동 배정은 되살리지 않는다 — 웰마다 사람이 고른다
   */
  const slotRows = slotGroups === undefined ? [] : slotWells(slotGroups)
  const fillerRows = Math.max(0, TRAY_SLOTS.length - slotRows.length)
  const firstEmptyWell = slotRows.find(({ wellName }) => holderOf(wellName) === undefined)?.wellName
  const slotTableView = (
    <div className="nbslots">
      <table className="a-tbl a-tbl--managed a-tbl--even">
        <caption className="a-sr">{t('stepper.newBatch.sample')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('column.well')}</th>
            <th scope="col">{t('batchNew.slot.group')}</th>
            {sampleColumns.map((column) => (
              <th key={column.key} scope="col">
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        {/*
          🔴 묶음마다 한 덩어리(`tbody`) — 같은 묶음의 웰 줄이 붙어 서고 덩어리 사이는 굵은 경계(eric 2026-10-29 「P1 두 개의 환자 번호가 같아야」).
             「묶음」 · 「환자 번호」 칸은 덩어리당 한 번(두 줄에 걸친 한 칸 · `rowSpan`) — 환자 번호는 그 묶음에 처음 고른 시료의 것이다.
             시료 번호 · 타입 · 암종 · 채혈 일시는 웰마다 그대로. 한 칸짜리 묶음(REF)은 한 줄 그대로
        */}
        {(slotGroups ?? []).map((group) => {
          const info = groupInfo.find((g) => g.sampleRef === group.sample_ref)
          const span = group.wells.length >= 2 ? group.wells.length : undefined
          return (
            <tbody
              className="nbslots__group"
              data-group={group.sample_ref}
              data-mismatch={info?.mismatch === true ? 'true' : undefined}
              key={group.sample_ref}
            >
              {group.wells.map((wellName, index) => {
                const sampleRef = group.sample_ref
                const id = holderOf(wellName)
                const held =
                  id === undefined ? undefined : available.find((s) => s.sample_id === id)
                /* 덩어리 둘째 줄부터는 걸친 칸(묶음 · 환자 번호)을 다시 세우지 않는다 */
                const spanned = span !== undefined && index > 0
                return (
                  <tr
                    className={shownWell === wellName ? 'nbslots__row--open' : undefined}
                    data-well={wellName}
                    key={wellName}
                  >
                    <td>{slotCell(wellName, true)}</td>
                    {spanned ? null : (
                      <td
                        className={span === undefined ? undefined : 'nbslots__span'}
                        rowSpan={span}
                      >
                        {slotCell(sampleRef, true)}
                      </td>
                    )}
                    {sampleColumns.map((column) => {
                      if (column.key === 'patient' && span !== undefined) {
                        if (spanned) return null
                        /* 묶음의 환자 — 어긋나면 그 자리에 ▲ + 글자(위험색) · 값은 가림 그대로 */
                        return (
                          <td
                            className={
                              info?.mismatch === true
                                ? 'nbslots__span nbslots__patient--mismatch'
                                : info?.patientId === undefined
                                  ? 'nbslots__span nbslots__vacantcell'
                                  : 'nbslots__span'
                            }
                            data-patient-cell={sampleRef}
                            key={column.key}
                            rowSpan={span}
                          >
                            {info?.mismatch === true ? (
                              <span className="nbslots__mismatch" role="alert">
                                <span aria-hidden="true">▲ </span>
                                {t('batchNew.slot.patientMismatch')}
                              </span>
                            ) : (
                              slotCell(
                                info?.patientId === undefined
                                  ? undefined
                                  : maskPii(info.patientId, view),
                                true,
                              )
                            )}
                          </td>
                        )
                      }
                      /* 시료 번호는 웰에 든 것(목록에 안 보여도) · 나머지는 목록 응답의 그 시료 */
                      const value =
                        column.key === 'sampleId'
                          ? id === undefined
                            ? undefined
                            : maskPii(id, view)
                          : held === undefined
                            ? undefined
                            : column.value(held, view)
                      const cell = slotCell(value, column.mono)
                      return (
                        <td
                          className={value === undefined ? 'nbslots__vacantcell' : undefined}
                          key={column.key}
                        >
                          {column.key === 'sampleId' && pickable ? (
                            <button
                              id={
                                wellName === firstEmptyWell ? sampleField.inputProps.id : undefined
                              }
                              aria-invalid={
                                wellName === firstEmptyWell && sampleField.inputProps.invalid
                              }
                              aria-describedby={
                                wellName === firstEmptyWell
                                  ? sampleField.inputProps.describedBy
                                  : undefined
                              }
                              aria-expanded={shownWell === wellName}
                              aria-haspopup="dialog"
                              aria-label={t('batchNew.slot.pick', { well: wellName })}
                              className="nbslots__pick"
                              onClick={(event) => openWellFromRow(wellName, event.currentTarget)}
                              type="button"
                            >
                              {cell}
                            </button>
                          ) : (
                            cell
                          )}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </tbody>
          )
        })}
        {/* 남는 자리 — 레시피를 안 골랐거나 자리가 9보다 적을 때 빈 틀로 높이를 지킨다(읽을 값이 없어 보조기술에는 숨긴다) */}
        {fillerRows === 0 ? null : (
          <tbody aria-hidden="true">
            {Array.from({ length: fillerRows }, (_, index) => (
              <tr className="nbslots__filler" key={`filler-${String(index)}`}>
                {Array.from({ length: sampleColumns.length + 2 }, (_, col) => (
                  <td className="nbslots__vacantcell" key={col}>
                    {slotCell(undefined)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        )}
      </table>
      {/*
        웰 선택 창 — 어느 웰을 눌러도 늘 같은 자리: 이 표 영역 위에 겹쳐 뜬다(표와 같은 위 끝 · 같은 폭 · 같은 높이 · eric 2026-10-27
        「오른쪽 부분에 동일하게 해줘.. 팝업을 띄우는데」). 어느 웰인지는 누른 웰 강조 · 창 제목이 말한다. 닫으면 그 자리에 표가 다시 보인다
      */}
      {shownWell === undefined || openRef === undefined ? null : (
        <WellSamplePicker
          controlGate={wellGate}
          emptyLabel={freeLabel}
          focusOnOpen={openWell !== undefined}
          key={shownWell}
          onClear={() => {
            setWellPicks((picks) => clearWell(picks, shownWell))
            closeWell()
          }}
          onClose={closeWell}
          onPick={(sampleId) => {
            const picks = pickWell(wellPicks, sampleId, shownWell)
            setWellPicks(picks)
            validation.change('samples', {
              ...validationValues,
              picks,
              selected: slotGroups === undefined ? selected : pickedInWellOrder(picks, slotGroups),
            })
            closeWell()
          }}
          picks={wellPicks}
          patientLock={
            slotGroups === undefined
              ? undefined
              : patientLockFor(slotGroups, wellPicks, shownWell, patientOf)
          }
          sampleRef={openRef}
          samples={available}
          assignedSamples={assignedSamples}
          onInspectAssigned={onInspectAssigned}
          view={view}
          wellName={shownWell}
        />
      )}
    </div>
  )

  const sampleSection = (
    <>
      {/* ── ① 시료 ─────────────────────────────────────────────────────── */}
      {/*
          🔴 용량 안내는 **안내이지 규칙이 아니다.** 서버가 상한을 재지 않으므로 화면도 막지 않는다 —
             9칸은 트레이의 물리 사실이라 사람이 알아야 할 뿐이다(팀장 결정 2026-09-20).
        */}
      <BatchSection
        framed={framed}
        /*
          창 안에서는 트레이 그림이 시료 자리를 직접 보인다 — 용량 안내 글은 그림의 접근 이름으로 간다(eric 2026-10-21 「이런 문구는 빼줘」).
          웰마다 하나씩 고르므로 자리보다 많이 담길 수 없다 — 넘침 경고가 없다
        */
        help={!framed ? t('batchNew.capacityHint') : undefined}
        title={
          <>
            {t('stepper.newBatch.sample')}{' '}
            <span className={framed ? 'nbcard__count' : 'muted'}>
              {capacity === undefined
                ? t('batchNew.quota', { picked: selected.length })
                : t('batchNew.quotaOf', { picked: selected.length, slots: capacity })}
            </span>
          </>
        }
      >
        {/*
          창 안: 트레이 그림 하나 — 시료 자리 웰을 누르면 그 웰 곁에 시료 선택 창이 뜬다(eric 2026-10-27 「해당 칸 또는 웰 별로」 · 「응 한번 해봐」).
          고른 시료 번호는 웰 안에 작게 선다. 위쪽 「기존 시료 추가」 단추 · 여러 건 담기 팝업 · 칸표 · 끌어 놓기는 걷었다
        */}
        {framed ? (
          <div className="nbtray nbtray--wells">
            {/* 트레이 상자 — 선택 창이 이 상자 기준으로 누른 웰 오른쪽에 겹쳐 선다(`.nbwells` · 위로 자란다) */}
            <div className="nbwells">
              <BatchTray
                captionOf={captionOf}
                /* 같은 묶음 칸을 한 틀로 감싼다 — 이 창에서만(상세 서랍 · 모니터링 그림은 그대로) */
                groupFrames
                groups={recipeDoc?.summary?.samples}
                {...(pickable
                  ? {
                      onWellPick: openWellAt,
                      openWell: shownWell,
                      wellLabel,
                    }
                  : {})}
                reason={trayReason}
              />
            </div>
            {slotTableView}
          </div>
        ) : (
          <>
            {plainTable}
            {sampleActions}
          </>
        )}

        {/* 페이지: 종전 그 자리 펼침 */}
        {pickerOpen && !framed ? (
          <div className="card" style={{ marginTop: 'var(--space-lg)' }}>
            <DataTable
              caption={t('action.pickExisting')}
              columns={freeColumns}
              density={density}
              emptyLabel={freeLabel}
              rowKey={(s) => s.sample_id}
              rows={free}
            />
          </div>
        ) : null}

        {/* 페이지(장비 관점 콘솔)에만 — 등록만 하고 담기는 목록에서(종전). 창 안에는 이 단추가 없다(eric 2026-10-27) */}
        {formOpen && !framed ? (
          <NewSampleForm
            controlGate={controlGate}
            error={registerError}
            hints={hints}
            onClose={() => setFormOpen(false)}
            registering={registering}
            {...(onRegisterSample === undefined ? {} : { onRegister: onRegisterSample })}
          />
        ) : null}
      </BatchSection>
    </>
  )

  const recipeSection = (
    <div
      className="nbrecipe-validation"
      id={framed ? recipeField.inputProps.id : undefined}
      aria-invalid={framed ? recipeField.inputProps.invalid : undefined}
      aria-describedby={framed ? recipeField.inputProps.describedBy : undefined}
      tabIndex={-1}
    >
      {/* ── ② 레시피 ───────────────────────────────────────────────────── */}
      {/*
          창 안: 제목은 「레시피」만 — 고른 레시피 이름을 제목 옆에 다시 적지 않는다(eric 2026-10-21 「이미 선택한 거에 보이잖아. 텍스트
          제거해줘」 — 고른 줄의 라디오 · 채움이 말한다). 페이지는 종전 「레시피」 줄 그대로다
        */}
      <BatchSection framed={framed} title={t('stepper.newBatch.recipe')}>
        {framed ? null : (
          <SettingsRow
            label={t('column.recipe')}
            value={
              recipeLabel === undefined ? (
                <span className="muted">{t('mask.none')}</span>
              ) : (
                <span>{recipeLabel}</span>
              )
            }
          />
        )}
        {/*
            🔴 **빈 상태를 「없음」 한 줄로 끝내지 않는다.** 실물 레시피가 0건이라 이 화면은
               지금 여기서 막힌다 — 어디서 만드는지를 같이 말한다.
          */}
        {recipes.length === 0 ? (
          <div style={{ paddingTop: 'var(--space-md)' }}>
            <EmptyState
              label={
                notices?.recipes ??
                (loading ? t('state.loading') : t('state.empty.committedRecipes'))
              }
            />
            {onNewRecipe === undefined ? null : (
              <div className="btnrow btnrow--end">
                <Button onClick={onNewRecipe} size="pill" variant="primary">
                  {t('action.newRecipe')}
                </Button>
              </div>
            )}
          </div>
        ) : framed ? (
          /*
           * 창 안 레시피 표 — 본문은 «세 줄»까지만 보이고 넘치면 이 상자 안에서만 구른다(열 머리는 위에 붙는다 · eric 2026-10-27 「레시피는 세 개만
           * 나오게 하고.. 세 개 넘으면 스크롤하게」). 고른 레시피가 상자 밖이면 그 줄이 보이게 굴린다(`recipeBox`). 페이지 판은 종전 그대로
           */
          <div className="nbrecipes" ref={recipeBox}>
            <DataTable
              caption={t('action.selectRecipe')}
              columns={recipeRadioColumns}
              density={density}
              hideCount
              {...tableWidths}
              emptyLabel={t('state.empty.committedRecipes')}
              rowKey={(row) => row.recipe_id}
              rows={recipes}
              onRowClick={(row: RecipeListRow) => {
                if (!recipePickGate.disabled) pickRecipe(row.recipe_id)
              }}
              selected={(row: RecipeListRow) => row.recipe_id === recipeId}
            />
          </div>
        ) : (
          <DataTable
            caption={t('action.selectRecipe')}
            columns={framed ? recipeRadioColumns : recipeColumns}
            density={density}
            hideCount={framed}
            {...tableWidths}
            emptyLabel={t('state.empty.committedRecipes')}
            rowKey={(row) => row.recipe_id}
            rows={recipes}
            {...(framed
              ? {
                  onRowClick: (row: RecipeListRow) => {
                    if (!recipePickGate.disabled) pickRecipe(row.recipe_id)
                  },
                  selected: (row: RecipeListRow) => row.recipe_id === recipeId,
                }
              : {})}
          />
        )}

        {/* 창 안: [상세] 팝업 — 고른 것과 무관하게 그 줄의 레시피를 읽기 전용으로. 고르기는 표의 라디오만(eric 2026-10-22) */}
        {framed && detailRow !== undefined ? (
          <RecipeDetailPopup
            detail={recipeDetail?.recipeId === detailRow.recipe_id ? recipeDetail : undefined}
            onClose={() => openDetail(undefined)}
            title={`${detailRow.name} · v${String(detailRow.version)}`}
          />
        ) : null}

        {/* 레시피 어휘 대조 — 고른 레시피의 사실이라 레시피 구역에 선다(사전 점검 구역은 만든 뒤에만 선다 · 2026-10-14) */}
        {recipe === undefined ? null : (
          <RecipeVocabularyReport note={vocabularyNotes[recipe.recipe_id]} />
        )}

        {/* 창 안에서는 바닥 줄 왼쪽(`AddActions error`)이 같은 글자를 세운다 */}
        <FormError message={createError} />
        {nameIgnored === undefined ? null : (
          <p role="status">
            {t('batchNew.name.ignored', {
              saved: nameIgnored.saved ?? t('mask.none'),
            })}
          </p>
        )}
        {/* 창 안에서는 이 자리가 비고 「추가」가 창 바닥 줄로 간다(아래 `ModalFooter`) */}
        {frame === 'modal' ? null : (
          <div className="btnrow btnrow--end" style={{ paddingTop: 'var(--space-lg)' }}>
            <Button onClick={submit} size="pill" variant="primary" {...createGateFinal}>
              {t('action.createBatch')}
            </Button>
          </div>
        )}
      </BatchSection>
    </div>
  )

  const form = (
    <div ref={validationRoot} className={frame === 'modal' ? 'apage apage--modal' : 'apage'}>
      {/*
          🔴 **한 열 폼**이다(2226행). 스테퍼는 진행 «표시»이고 세 구역은 처음부터 다 보인다 —
             단계별로 감추는 마법사가 아니다. 스테퍼도 구역과 같은 폭 안에 들어가야
             줄이 맞는다(예전에는 폼 밖에 있어 혼자 화면 끝까지 늘어났다).
        */}
      <div className="oneform">
        {/*
          🔴 창 안에서는 번호 스테퍼를 뺀다(팀장 결정 2026-09-30 · 추가 창 일곱의 규격) — 구획 차례가 같은 말을 한다.
             페이지(장비 관점 콘솔)는 종전 그대로 선다.
        */}
        {frame === 'modal' ? null : (
          <NumberStepper
            current={recipe === undefined ? 0 : 1}
            label={t('screen.batchNew.title')}
            steps={[t('stepper.newBatch.sample'), t('stepper.newBatch.recipe')]}
          />
        )}
        {/*
            ── 작업 이름(선택) ── `docs/backend-requests.csv` BR-94. 🔴 비우면 **키를 안 싣고** 서버가 조립한다.
               지금 서버는 이 칸을 버린다 — 버려졌으면 만든 뒤 결과 자리에서 말한다.
          */}
        {/*
          창 안: 레시피 추가 창처럼 큰 카드 «하나» 안에 구역 셋을 옅은 선으로 가른다(eric 2026-10-21 「레시피 추가 화면 느낌 컨셉으로」 ·
          `newBatch.css`). 구역 제목은 작은 글자(레시피 추가의 「공통」 · 「트레이」 자리). 페이지는 종전 구역 그대로
        */}
        <BatchSections framed={framed}>
          {/*
            창 안: 「공통」 아래 「이름」 한 줄 — 라벨 왼쪽 | 입력 오른쪽 끝(eric 2026-10-22 「이름 인풋 창으로 오른쪽 정렬해줘」 · `newBatch.css`).
            「비우면 … 자동」 안내 줄은 걷었다(eric 2026-10-21 「이 문구 빼주고」) — 비우면 서버가 조립하는 동작은 그대로다(`batchNameBody`)
          */}
          <BatchSection framed={framed} {...(framed ? { title: t('section.common') } : {})}>
            <div className="nbname">
              <Field {...nameField.fieldProps} label={t('batchNew.name.label')}>
                <TextInput
                  {...nameField.inputProps}
                  onChange={(name) => {
                    setBatchName(name)
                    validation.change('name', { ...validationValues, name })
                  }}
                  value={batchName}
                  wide
                  {...(!nameAccepted
                    ? ({
                        disabled: true,
                        disabledReason: t('reason.batchNameUnsupported'),
                      } as const)
                    : made
                      ? ({
                          disabled: true,
                          disabledReason: t('reason.batchAlreadyCreated'),
                        } as const)
                      : controlGate)}
                />
              </Field>
            </div>
          </BatchSection>

          {/*
            창 안: 레시피 → 시료 차례(eric 2026-10-21 「레시피를 먼저 고르게 하고.. 그 아래에.. 시료를 고르게 해줘」) — 레시피가 칸을 정한다.
            페이지(장비 관점 콘솔)는 종전 차례(시료 → 레시피) 그대로다
          */}
          {framed ? (
            <>
              {recipeSection}
              {recipeField.fieldProps.error ? (
                <p className="a-field__error" id={recipeField.fieldProps.errorId} role="alert">
                  {recipeField.fieldProps.error}
                </p>
              ) : null}
              {sampleSection}
              {sampleField.fieldProps.error ? (
                <p className="a-field__error" id={sampleField.fieldProps.errorId} role="alert">
                  {sampleField.fieldProps.error}
                </p>
              ) : null}
            </>
          ) : (
            <>
              {sampleSection}
              {recipeSection}
            </>
          )}
        </BatchSections>

        {/*
          🔴 **사전 점검 구역을 걷었다**(eric 2026-10-14 「작업 추가 창의 사전 점검은 빼줘」). 점검은 작업 줄 [시작]이 «시작 직전»에
             돌린다(`useStartGate` — 점검 → 차단 · 경고 확인 창 → 시작) — 그 결과가 효력이 있는 점검이다. 추가 창에서 한 번 더 미리 보이면
             「만들 때 하는 점검인가 · 시작할 때 하는 점검인가」가 헷갈렸고, 시뮬 준비도 두 번 돌았다. 만든 뒤에는 작업 열기만 남는다.
          🔴 「작업 열기」는 페이지 판(장비 관점 콘솔)만이다 — 창은 만들어지면 그 자리에서 닫히고 목록으로 간다(eric 2026-10-21 「작업 추가하면
             창이 바로 닫혀야지. 왜 상세 버튼이 있어?」 · `BatchNewContainer`)
        */}
        {framed || !made || onOpenBatch === undefined ? null : (
          <div className="btnrow btnrow--end">
            <Button onClick={onOpenBatch} size="pill" variant="primary">
              {t('action.openBatch')}
            </Button>
          </div>
        )}
      </div>
      {/*
        「추가」 창 넷의 바닥 줄(eric 2026-09-30 「포맷을 모두 동일하게」) — 「취소」 · 「추가」. 🔴 만든 뒤에도
        「추가」는 비활성 + 사유로 남는다(두 번 만들지 않는다). 사전 점검 · 작업 열기는 ③ 구역에 그대로 있다.
      */}
      {frame === 'modal' ? (
        <ModalFooter>
          {lockControl}
          <AddActions
            busy={creating}
            error={createError}
            framed
            onCancel={() => onCancel?.()}
            onSubmit={submit}
            pageLabel={t('action.createBatch')}
            /* 「레시피와 시료가 있어야 합니다」는 바닥 글자로 세우지 않는다(eric 2026-10-21 「이거 빼줘 텍스트」) — 툴팁 · 보조기술은 그대로 */
            quietReasons={[
              t('reason.needRecipeAndSample'),
              ...(lockControl == null ? [] : [t('reason.lockRequired')]),
            ]}
            submitGate={createGateFinal}
          />
        </ModalFooter>
      ) : null}
    </div>
  )

  if (frame === 'modal') return form

  return (
    <div className="apageframe">
      <PageHeader
        actions={
          <Button onClick={onCancel} size="pill">
            {t('action.cancel')}
          </Button>
        }
        back={
          <button
            aria-label={`${t('action.back')} ${t('screen.batches.title')}`}
            className="back lnk"
            onClick={onCancel}
            type="button"
          >
            <span aria-hidden="true">←</span> {t('screen.batches.title')}
          </button>
        }
        title={t('screen.batchNew.title')}
      />

      {form}
    </div>
  )
}

/**
 * 새 작업의 구역 하나. 🔴 **창 안에서는 추가 창 일곱의 구획(`FormSection`)이다**(eric 2026-09-30 「창마다 포맷이 달라..
 * 다 통일해줘」). 페이지(장비 관점 콘솔)는 종전 구역(`.sect`) 그대로다 — `FormSection` 의 페이지 모양은 칸 폭 상한이
 * 있어 이 화면의 표를 누른다.
 */
function BatchSection({
  framed,
  title,
  help,
  actions,
  children,
}: {
  framed: boolean
  title?: ReactNode
  help?: string | undefined
  /** 창 안 — 구역 제목 줄 오른끝 단추(시료 구역의 「기존 시료에서」) */
  actions?: ReactNode
  children: ReactNode
}) {
  if (framed)
    return (
      <FormSection actions={actions} help={help} title={title} wide>
        {children}
      </FormSection>
    )
  return (
    <section className="sect">
      {title === undefined ? null : <div className="sect__title">{title}</div>}
      {help === undefined ? null : <p className="muted">{help}</p>}
      {children}
    </section>
  )
}

/**
 * 창 안의 큰 카드 하나 — 레시피 추가 창의 본문 카드와 같은 면(`.card` 와 같은 토큰)이고, 안의 구역은 옅은 선으로 갈린다(`newBatch.css`).
 * ⚠ `.card` 클래스를 달지 않는다 — `.card` 는 묶음(팝업 안 레시피 본문 · 페이지의 펼침)을 가리키는 데 쓰인다. 큰 카드까지 `.card` 면
 *   그 선택자가 이름 칸을 잡는다(2026-10-21 시험 회귀).
 * 🔴 카드는 `div` 다 — 창 본문의 맨 바깥 구획은 `FormSection`(`section.fsec`)이어야 한다(추가 창 규격 · `addFormSpec.test`).
 * 페이지(장비 관점 콘솔)에서는 아무것도 감싸지 않는다.
 */
function BatchSections({ framed, children }: { framed: boolean; children: ReactNode }) {
  if (!framed) return children
  return <div className="nbcard">{children}</div>
}
